/**
 * Snapshot of local data for the active user (guest or logged-in) and its
 * transactional, idempotent import on the receiving device.
 *
 * - The snapshot consists of the MATERIALIZED ROWS from user domain tables
 *   written by the CLIENT (`P2P_TRANSFER_TABLES`). ⚠️ CC-33: excludes
 *   `routine_version` (server-materialized history, client read-only):
 *   the server re-derives it when re-importing routines; transferring it would
 *   generate writes that the client cannot synchronize. No need to transfer
 *   the `ps_crud` queue:
 *   upon insertion via the PowerSync API, the receiver generates their OWN
 *   equivalent upload operations (complete rows), so pending sender changes
 *   travel already materialized in their rows.
 * - Import: re-stamps `user_id` to the receiver's user (same pattern
 *   as `sweepVisitorData`) and only inserts rows whose `id` does not exist →
 *   re-importing is a no-op (idempotency by id).
 * - Security: snapshot comes from outside → tables ONLY from allowlist and
 *   columns validated against an identifier pattern before interpolation.
 */

import { P2P_TRANSFER_TABLES } from "@gymlog/powersync";
import { isRecord } from "./guards";

/** Minimal DB surface (PowerSync satisfies it; tests inject a fake). */
export interface SnapshotLockContext {
  getAll<T>(sql: string, params?: unknown[]): Promise<T[]>;
  execute(sql: string, params?: unknown[]): Promise<unknown>;
}

export interface SnapshotDb extends SnapshotLockContext {
  writeTransaction<T>(fn: (tx: SnapshotLockContext) => Promise<T>): Promise<T>;
}

export const SNAPSHOT_VERSION = 1;

export interface TransferSnapshot {
  version: number;
  tables: Record<string, Record<string, unknown>[]>;
}

export interface ImportCounts {
  inserted: number;
  skipped: number;
}

const IDENTIFIER_RE = /^[A-Za-z_][A-Za-z0-9_]*$/u;

export async function buildSnapshot(
  db: SnapshotLockContext,
  userId: string,
  tables: readonly string[] = P2P_TRANSFER_TABLES,
): Promise<TransferSnapshot> {
  const snapshot: TransferSnapshot = { version: SNAPSHOT_VERSION, tables: {} };
  for (const table of tables) {
    if (!IDENTIFIER_RE.test(table)) {
      throw new Error(`Invalid table name: ${table}`);
    }
    snapshot.tables[table] = await db.getAll<Record<string, unknown>>(
      `SELECT * FROM ${table} WHERE user_id = ?`,
      [userId],
    );
  }
  return snapshot;
}

export function snapshotRowCount(snapshot: TransferSnapshot): number {
  return Object.values(snapshot.tables).reduce((sum, rows) => sum + rows.length, 0);
}

export function parseSnapshotJson(json: string): TransferSnapshot | null {
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    return null;
  }
  if (!isRecord(parsed) || parsed.version !== SNAPSHOT_VERSION || !isRecord(parsed.tables)) {
    return null;
  }
  // Reconstruct snapshot validating each row (untrusted JSON):
  // no type assertions, only what passes guards gets in.
  const tables: Record<string, Record<string, unknown>[]> = {};
  for (const [name, rows] of Object.entries(parsed.tables)) {
    if (!Array.isArray(rows)) {
      return null;
    }
    const validRows: Record<string, unknown>[] = [];
    const rowList: unknown[] = rows;
    for (const row of rowList) {
      if (!isRecord(row)) {
        return null;
      }
      validRows.push(row);
    }
    tables[name] = validRows;
  }
  return { version: SNAPSHOT_VERSION, tables };
}

/**
 * Imports snapshot in a single write transaction.
 *
 * Idempotent: rows whose `id` already exists locally are skipped (local data
 * is never overwritten). Imported rows are re-stamped with `receiverUserId`,
 * so that (a) local UI displays them to the active user and (b) if the
 * receiver is logged in, the PowerSync queue uploads them with the correct owner.
 */
export async function importSnapshot(
  db: SnapshotDb,
  snapshot: TransferSnapshot,
  receiverUserId: string,
  allowedTables: readonly string[] = P2P_TRANSFER_TABLES,
): Promise<ImportCounts> {
  if (!receiverUserId) {
    throw new Error("receiverUserId is required");
  }
  const counts: ImportCounts = { inserted: 0, skipped: 0 };

  await db.writeTransaction(async (tx) => {
    // Allowlist order (parents before children), not snapshot order.
    for (const table of allowedTables) {
      const rows = snapshot.tables[table];
      if (!rows?.length) {
        continue;
      }

      // Sender/receiver version drift: only insert columns that
      // exist locally (PRAGMA works across PowerSync views as well).
      // Without rows or pragma → no extra filter.
      const localColumns = new Set(
        (await tx.getAll<{ name: string }>(`PRAGMA table_info(${table})`)).map((c) => c.name),
      );

      const incomingIds = rows
        .map((row) => row.id)
        .filter((id): id is string => typeof id === "string" && id.length > 0);
      const existing = new Set<string>();
      const BATCH = 200;
      for (let i = 0; i < incomingIds.length; i += BATCH) {
        const batch = incomingIds.slice(i, i + BATCH);
        const placeholders = batch.map(() => "?").join(",");
        const found = await tx.getAll<{ id: string }>(
          `SELECT id FROM ${table} WHERE id IN (${placeholders})`,
          batch,
        );
        for (const row of found) {
          existing.add(row.id);
        }
      }

      for (const row of rows) {
        const { id } = row;
        if (typeof id !== "string" || id.length === 0) {
          counts.skipped += 1;
          continue;
        }
        if (existing.has(id)) {
          counts.skipped += 1;
          continue;
        }
        const stamped: Record<string, unknown> = { ...row };
        if ("user_id" in stamped) {
          stamped.user_id = receiverUserId;
        }
        const columns = Object.keys(stamped).filter(
          (c) => IDENTIFIER_RE.test(c) && (localColumns.size === 0 || localColumns.has(c)),
        );
        if (columns.length === 0) {
          counts.skipped += 1;
          continue;
        }
        const placeholders = columns.map(() => "?").join(", ");
        await tx.execute(
          `INSERT INTO ${table} (${columns.join(", ")}) VALUES (${placeholders})`,
          columns.map((c) => stamped[c] ?? null),
        );
        counts.inserted += 1;
      }
    }
  });

  return counts;
}
